import { kFieldLabelSelector } from "@protontech/autofill/constants/selectors";

export const closestParent = (start: HTMLElement, match: (el: HTMLElement) => boolean): HTMLElement | null => {
  const parent = start.parentElement;
  if (!parent) return null;
  return match(parent) ? parent : closestParent(parent, match);
};

export const parentQuery = (start: HTMLElement, match: (parent: HTMLElement) => HTMLElement | null, maxIterations = 1): HTMLElement | null => {
  const parent = start?.parentElement;
  if (!parent) return null;
  const result = match(parent);
  return result || maxIterations <= 0 ? result : parentQuery(parent, match, maxIterations - 1);
};

export const walkUpWhile =
  (start: HTMLElement, maxIterations: number) =>
  (check: (parent: HTMLElement, candidate: HTMLElement) => boolean): HTMLElement => {
    const parent = start.parentElement;
    if (maxIterations <= 0 || parent === null) return start;

    return check(parent, start) ? walkUpWhile(parent, maxIterations - 1)(check) : start;
  };

/* resolves the nth parent element of a DOM node if
 * it exists, otherwise returns current node */
export const getNthParent =
  (el: HTMLElement) =>
  (n: number): HTMLElement => {
    const parent = el.parentElement;
    return parent === null || n === 0 ? el : getNthParent(parent)(n - 1);
  };

/* filters out duplicate elements */
export const uniqueNodes = (...nodes: HTMLElement[][]) => Array.from(new Set(nodes.flat()));

/* returns the DOM Rect for a given DOM node and
 * computes the area in px^2 taken by the node */
export const getNodeRect = (el: HTMLElement) => {
  const { height, width, top, bottom } = el.getBoundingClientRect();
  const area = height * width;

  return { height, width, top, bottom, area };
};

export const matchPreviousSibling = (el: Element, match: (el: Element) => boolean): Element | null => {
  const prevEl = el.previousElementSibling;
  if (prevEl === null) return null;
  if (match(prevEl)) return prevEl;
  return matchPreviousSibling(prevEl, match);
};

export const matchPreviousNonEmptySibling = (el: Element) => matchPreviousSibling(el, (el) => el instanceof HTMLElement && el.innerText.trim().length > 0);

/* Returns a field's label if any. Falls back to the
 * closest label if none found for the current element's
 * id. Defaults to the inputs parent element if no match */
export const getLabelFor = (el: HTMLElement): HTMLElement | null => {
  const forId = el.getAttribute("id") ?? el.getAttribute("name");

  /** 1. Direct label match */
  const label = document.querySelector<HTMLElement>(`label[for="${forId}"]`);
  if (label) return label;

  /** 2. Find enclosing ancestor label */
  const parentLabel = el.closest("label");
  if (parentLabel) return parentLabel;

  /* 3. Find sibling label if not ancestor (eg: form in a `table` layout
   * where the label will be located in sibling `<td>` */
  const closestLabel = parentQuery(el, (parent) => parent.querySelector("label:not([for])"), 1);
  if (closestLabel) return closestLabel;

  /** 4. Find sibling label-like element if no labels found */
  const labelLike = parentQuery(el, (parent) => parent.querySelector(kFieldLabelSelector), 1);
  if (labelLike) return labelLike;

  /* 5. Fallback to the first previous sibling element with some inner text */
  const textNodeAbove = matchPreviousNonEmptySibling(el);
  if (textNodeAbove) return textNodeAbove as HTMLElement;

  return null;
};

/* Given a DOM Rect, returns the `{x, y}` coordinates of its center */
export const getRectCenter = (rect: DOMRect): { x: number; y: number } => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

export const getRectMinDistance = (rectA: DOMRect, rectB: DOMRect): { dx: number; dy: number } => {
  const centerA = getRectCenter(rectA);
  const centerB = getRectCenter(rectB);

  const dx = Math.abs(centerA.x - centerB.x) - (rectA.width + rectB.width) / 2;
  const dy = Math.abs(centerA.y - centerB.y) - (rectA.height + rectB.height) / 2;

  return { dx, dy };
};

export const pruneNested = (els: HTMLElement[]) => {
  return els.reduce<HTMLElement[]>((acc, el) => {
    for (let i = 0; i <= acc.length - 1; i++) {
      if (acc[i] === el) continue;
      if (acc[i].contains(el)) return acc;
      if (el.contains(acc[i])) {
        acc[i] = el;
        return acc;
      }
    }

    acc.push(el);
    return acc;
  }, []);
};

/* recursively finds the most common ancestor between
 * two HTMLElement nodes. If no parent element found,
 * early return as we're most likely on the root node */
export const getCommonAncestor = (elementA: HTMLElement, elementB: HTMLElement): HTMLElement => {
  if (elementA === elementB) return elementA;
  return elementA.contains(elementB) ? elementA : elementA.parentElement ? getCommonAncestor(elementA.parentElement, elementB) : elementA;
};

/* Recursively finds the first parent element that is positioned
 * as either absolute or fixed. Pushes the results into the cache
 * parameter for performance reasons, as we aim to avoid unnecessary
 * traversals of the DOM tree. */
const findStackedParent = (el: HTMLElement, cache: HTMLElement[] = [], maxIterations: number): HTMLElement | null => {
  if (cache.some((group) => group.contains(el))) return null;

  const parent = el.parentElement;
  if (maxIterations === 0 || !parent) return null;

  const computedStyle = getComputedStyle(parent);
  const position = computedStyle.getPropertyValue("position");

  if (position === "fixed" || position === "absolute") {
    cache.push(parent);
    return parent;
  }

  return findStackedParent(parent, cache, maxIterations - 1);
};

export const findStackedParents = (els: HTMLElement[], maxIterations: number) => {
  const cache: HTMLElement[] = [];
  return els.map((input) => findStackedParent(input, cache, maxIterations)).filter((el): el is HTMLElement => Boolean(el && el !== document.body));
};
